import React, { useEffect, useState, useMemo, useRef } from 'react'
import { BasePage as CngBasePage } from 'cng-web-lib'
import MpciApiClient from '../Common/services/MpciApiClient'
import DashboardService from './DashboardService'
import { Grid, Box, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'
import MetricsCard from './MetricsCard'
import AnnouncementBanner from './AnnouncementBanner'
import FilingsByStatusChart from './FilingsByStatusChart'
import BILL_STATUSES from '../Common/constants/billStatuses'
import { getChartColors } from '../Common/constants/colors'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'

// Maps status key → { label, countField, trendField, statusKey }
const KPI_CONFIG = [
  {
    key:        'SUBM',
    label:      'Submitted to NAIC',
    countField: 'subm30DaysCount',
    trendField: 'submWeeklyDiffCount',
    statusKey:  BILL_STATUSES.SUBMITTED_TO_NAIC
  },
  {
    key:        'ACT',
    label:      'Assessment Complete',
    countField: 'act30DaysCount',
    trendField: 'actWeeklyDiffCount',
    statusKey:  BILL_STATUSES.ASSESSMENT_COMPLETE
  },
  {
    key:        'RFI',
    label:      'Pending Action (RFI)',
    countField: 'rfi30DaysCount',
    trendField: 'rfiWeeklyDiffCount',
    statusKey:  BILL_STATUSES.REQUEST_FOR_INFO
  },
  {
    key:        'DNL',
    label:      'Do Not Load',
    countField: 'dnl30DaysCount',
    trendField: 'dnlWeeklyDiffCount',
    statusKey:  BILL_STATUSES.DO_NOT_LOAD
  },
  {
    key:        'CONTRL_NACK',
    label:      'Failed Acknowledgement',
    countField: 'contrlNack30DaysCount',
    trendField: 'contrlNackWeeklyDiffCount',
    statusKey:  BILL_STATUSES.FAILED_ACK
  },
  {
    key:        'SUBM_ERR',
    label:      'Error in Submission',
    countField: 'submErr30DaysCount',
    trendField: 'submErrWeeklyDiffCount',
    statusKey:  BILL_STATUSES.ERROR_IN_SUBMISSION
  }
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  }
}))

function DashboardContent({ showNotification }) {
  const classes = useStyles()
  const theme = useTheme()
    const [metrics, setMetrics] = useState([])
  const [kpiLoading, setKpiLoading] = useState(true)
  const [announcement, setAnnouncement] = useState(null)
  const mountedRef = useRef(true)

  // Resolve chart colors from theme
  const chartColors = useMemo(() => getChartColors(theme), [theme])

    useEffect(() => {
    mountedRef.current = true
    setKpiLoading(true)
    MpciApiClient.get(
      UaeMpciApiUrls.DASHBOARD_KPI,
      (data) => {
        if (!mountedRef.current) return
        // API: flat object { subm30DaysCount, act30DaysCount, submWeeklyDiffCount, ... }
                const mapped = KPI_CONFIG.map((cfg) => ({
          label:     cfg.label,
          colorKey:  cfg.key,        // BE status code — used to look up chart color
          statusKey: cfg.statusKey,
          count:     data?.[cfg.countField] ?? 0,
          trend:     data?.[cfg.trendField] ?? 0
        }))
        setMetrics(mapped)
        setKpiLoading(false)
      },
      (error) => {
        if (!mountedRef.current) return
        console.error('[Dashboard] KPI fetch failed', error)
        setKpiLoading(false)
      }
    )
          return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    DashboardService.fetchAnnouncement(
      (data) => { if (mountedRef.current) setAnnouncement(data || null) },
      (error) => { console.error('[Dashboard] Announcement fetch failed', error) }
    )
  }, [])

  return (
    <Box className={classes.root}>
      <AnnouncementBanner announcement={announcement} />
      <Box mt={3}>
        <Typography variant='h6' className={classes.sectionTitle}>
          Overview - Last 30 Days
        </Typography>
                        <Grid container spacing={2}>
          {metrics.map((metric) => (
            <Grid item xs={12} sm={6} md style={{ flexGrow: 1, flexBasis: 0 }} key={metric.label}>
              <MetricsCard
                label={metric.label}
                count={metric.count}
                trend={metric.trend}
                color={chartColors[metric.colorKey] || theme.palette.primary.main}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box mt={3}>
        <FilingsByStatusChart />
      </Box>
    </Box>
  )
}

function DashboardPage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  return (
    <CngBasePage
      renderPage={(showSnackbar) => (
        <DashboardContent showNotification={showSnackbar} {...props} />
      )}
      extraCngBreadcrumbNameMap={breadcrumbNameMap}
    />
  )
}

export default DashboardPage
